import type {
  CarePlanBranch,
  CarePlanStep,
} from "@/components/care-plan/types";
import { StepType } from "@/components/care-plan/types";

import type { CarePlanCycleDetail } from "@/types/care-plan";

const CYCLE_COLORS = [
  {
    bg: "bg-status-info-bg",
    border: "rgb(var(--status-info-text))",
    text: "text-status-info-text",
  },
  {
    bg: "bg-status-purple-bg",
    border: "rgb(var(--status-purple-text))",
    text: "text-status-purple-text",
  },
  {
    bg: "bg-status-emerald-bg",
    border: "rgb(var(--status-emerald-text))",
    text: "text-status-emerald-text",
  },
  {
    bg: "bg-status-orange-bg",
    border: "rgb(var(--status-orange-text))",
    text: "text-status-orange-text",
  },
  {
    bg: "bg-status-pink-bg",
    border: "rgb(var(--status-pink-text))",
    text: "text-status-pink-text",
  },
  {
    bg: "bg-status-indigo-bg",
    border: "rgb(var(--status-indigo-text))",
    text: "text-status-indigo-text",
  },
] as const;

export type CycleColor = (typeof CYCLE_COLORS)[number];

export function getCycleColor(index: number): CycleColor {
  return CYCLE_COLORS[index % CYCLE_COLORS.length];
}

/**
 * Build a `branchId -> logical-branch-key` map.
 *
 * Branches that are pass-clones of each other (created when a decision
 * step's outer cycle materializes passes 2+) have distinct `branchId`s but
 * represent the same logical branch.  We identify the logical branch by
 * `(rootDecisionStepId, branchPosition)` — walking the decision step's
 * `sourceStepId` chain back to the original so clones resolve to the same
 * key.  Sibling branches of one decision step keep different keys because
 * their positions differ.
 */
function buildLogicalBranchKeyMap(steps: CarePlanStep[]): Map<string, string> {
  const stepById = new Map<string, CarePlanStep>();
  const branches: CarePlanBranch[] = [];

  const walk = (list: CarePlanStep[]) => {
    for (const step of list) {
      stepById.set(step.id, step);
      if (step.type === StepType.DECISION && step.branches) {
        for (const branch of step.branches) {
          branches.push(branch);
          walk(branch.steps);
        }
      }
    }
  };
  walk(steps);

  const rootDecisionStepId = (stepId: string): string => {
    let currentId = stepId;
    let current = stepById.get(currentId);
    while (current?.sourceStepId) {
      const next = stepById.get(current.sourceStepId);
      if (!next) break;
      currentId = current.sourceStepId;
      current = next;
    }
    return currentId;
  };

  const map = new Map<string, string>();
  for (const branch of branches) {
    const rootId = rootDecisionStepId(branch.decisionStepId);
    map.set(branch.id, `${rootId}:${branch.position}`);
  }
  return map;
}

/**
 * Group cycles into "families" — cycles that represent the same logical cycle
 * across different materialized passes.
 *
 * Walks the `parentCycleId` chain up to the root ancestor so that
 * multi-level nested cycles are grouped correctly. For example, if cycle A
 * is cloned as A', and each has a child (B under A, B' under A'), then B
 * and B' share the same root ancestor (A) and belong to the same family.
 *
 * Branch scope is part of the key: cycles in different *logical* branches
 * of a decision step share the same parent cycle but are distinct logical
 * cycles and must not be merged.  Pass-clones of the same logical branch
 * resolve to the same key so their inner cycles still group correctly.
 *
 * Root cycles (no parent) are each their own family.
 */
export function getCycleFamilies(
  cycles: CarePlanCycleDetail[],
  steps: CarePlanStep[] = [],
): {
  displayCycles: CarePlanCycleDetail[];
  familyMap: Map<string, Set<string>>;
  cycleToDisplayIndex: Map<string, number>;
  cycleToParentPass: Map<string, number>;
} {
  // Build a lookup so we can walk the parent chain.
  const cycleById = new Map<string, CarePlanCycleDetail>();
  for (const cycle of cycles) {
    cycleById.set(cycle.id, cycle);
  }

  const logicalBranchKey = buildLogicalBranchKeyMap(steps);

  // Walk the parentCycleId chain to find the root ancestor for a cycle.
  // Root cycles return their own id.
  function getRootAncestorId(cycle: CarePlanCycleDetail): string {
    let current = cycle;
    while (current.parentCycleId) {
      const parent = cycleById.get(current.parentCycleId);
      if (!parent) break;
      current = parent;
    }
    return current.id;
  }

  // Group by (root ancestor id, depth, logical branch key). Cycles that
  // share a root ancestor at the same depth within the same logical branch
  // are clones of the same logical nested cycle. Cycles in different
  // branches of a decision step resolve to different logical branch keys
  // and stay in separate families.
  const groups = new Map<string, CarePlanCycleDetail[]>();
  for (const cycle of cycles) {
    const rootId = getRootAncestorId(cycle);
    // Depth distinguishes the root cycle itself from its nested children.
    let depth = 0;
    let current: CarePlanCycleDetail | undefined = cycle;
    while (current?.parentCycleId) {
      depth++;
      current = cycleById.get(current.parentCycleId);
    }
    const branchKey = cycle.branchId
      ? (logicalBranchKey.get(cycle.branchId) ?? cycle.branchId)
      : "";
    const key =
      depth === 0
        ? `${rootId}:${branchKey}`
        : `child:${rootId}:${depth}:${branchKey}`;
    const group = groups.get(key) ?? [];
    group.push(cycle);
    groups.set(key, group);
  }

  const displayCycles: CarePlanCycleDetail[] = [];
  const familyMap = new Map<string, Set<string>>();
  const cycleToDisplayIndex = new Map<string, number>();
  // For child cycles, maps cycleId → which pass of the parent it represents.
  // Original child = pass 1, first clone = pass 2, etc.
  const cycleToParentPass = new Map<string, number>();

  let displayIndex = 0;
  const groupEntries = Array.from(groups.values());
  for (const group of groupEntries) {
    const sorted = [...group].sort(
      (a, b) =>
        new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
    );
    displayCycles.push(sorted[0]);

    const familyIds = new Set(group.map((c: CarePlanCycleDetail) => c.id));
    for (let i = 0; i < sorted.length; i++) {
      const cycle = sorted[i];
      familyMap.set(cycle.id, familyIds);
      cycleToDisplayIndex.set(cycle.id, displayIndex);
      if (cycle.parentCycleId) {
        cycleToParentPass.set(cycle.id, i + 1);
      }
    }
    displayIndex++;
  }

  return { displayCycles, familyMap, cycleToDisplayIndex, cycleToParentPass };
}

export function getCycleColorIndex(
  cycleId: string,
  cycles?: CarePlanCycleDetail[],
): number {
  if (cycles && cycles.length > 0) {
    const idx = cycles.findIndex((c) => c.id === cycleId);
    if (idx !== -1) return idx;
  }
  // Stable fallback: derive index from cycleId hash
  let hash = 0;
  for (let i = 0; i < cycleId.length; i++) {
    hash = (hash * 31 + cycleId.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % CYCLE_COLORS.length;
}
